import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom, useAtomValue } from "jotai";
import { Icon } from "@common/index";
import { ROUTES } from "@routing/routes";
import {
    categoriesAtom,
    categoriesFetchErrorAtom,
    isFetchingCategoriesAtom,
} from "@/store/categoryAtoms";
import {
    productSuggestionsAtom,
    productSuggestionsStatusAtom,
} from "@/store/productSuggestionAtoms";
import { useCategoryApi } from "@/hooks/apiHooks/useCategoryApi";
import { useProductSuggestApi } from "@/hooks/apiHooks/useProductSuggestApi";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";
import type { ProductSuggestion } from "@models/productSuggestion";
import { isSearchPanelOpenAtom } from "./store/searchAtoms";
import { useRecentSearches } from "./hooks/useRecentSearches";
import { flattenCategories } from "./flattenCategories";
import {
    buildAutocompleteOptions,
    optionElementId,
    productsLiveMessage,
    type AutocompleteOption,
} from "./_helpers/autocompleteOptions";
import { SearchInput } from "./SearchInput";
import { RecentSearches } from "./RecentSearches";
import { BrowseCategories } from "./_components/BrowseCategories";
import { Autocomplete } from "./Autocomplete";

// Where focus lands on open: the query input (SearchInput's combobox).
const SEARCH_INPUT_SELECTOR = 'input[role="combobox"]';

// The API's own floor: below two normalized characters GET /products/suggest answers [] without
// searching, so a one-character query fetches nothing and shows no Products section at all —
// not an empty one, and not a failed one.
const PRODUCT_SUGGEST_MIN_LENGTH = 2;

// Trailing debounce on the product FETCH only (the story's 200 ms). Keystrokes update the input
// and the local category matches immediately; only the request waits, so one typing burst is one
// request. Same shape as SearchResults' filter debounce.
const PRODUCT_SUGGEST_DEBOUNCE_MS = 200;

// The query a product request is issued for: trimmed, and "" below the floor.
const toSuggestQuery = (value: string): string => {
    const trimmed = value.trim();
    return trimmed.length >= PRODUCT_SUGGEST_MIN_LENGTH ? trimmed : "";
};

const LISTBOX_ID = "search-autocomplete-listbox";

// Below the floor the Products section is absent whatever the atoms hold — they are cleared by
// the effect below, but that runs after the commit in which the query dropped, and the section
// must already be gone in that commit. A module-scope constant so the useMemo below sees one
// identity, not a fresh [] per render.
const NO_PRODUCTS: ProductSuggestion[] = [];
// Same reason, for the option list of an empty query (see `options` below).
const NO_OPTIONS: AutocompleteOption[] = [];

export const SearchPanel = () => {
    const [isOpen, setIsOpen] = useAtom(isSearchPanelOpenAtom);
    const [query, setQuery] = useState("");
    // Index into `options` of the keyboard-highlighted row; -1 for none.
    const [activeIndex, setActiveIndex] = useState(-1);
    // The query whose product request last settled (loaded or failed), or "" when none has
    // since the query was last below the floor. `isPending` below is derived from it: a query
    // that is not the settled one has a request armed (the debounce timer) or on the wire.
    // Owned here rather than read from the hook's status atom, because the atom only learns
    // about a query when the timer fires 200 ms later — and the shopper must see "Searching
    // products…" from the keystroke, never "No matches." first. Derived rather than set in an
    // effect so it is right in the keystroke's own commit, with no frame in between.
    const [settledQuery, setSettledQuery] = useState("");
    const inputRef = useRef<HTMLInputElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const { items: recent, push: pushRecent } = useRecentSearches();
    const categories = useAtomValue(categoriesAtom);
    const isFetchingCategories = useAtomValue(isFetchingCategoriesAtom);
    const categoriesFetchError = useAtomValue(categoriesFetchErrorAtom);
    const { getCategories } = useCategoryApi();
    const productSuggestions = useAtomValue(productSuggestionsAtom);
    const productsStatus = useAtomValue(productSuggestionsStatusAtom);
    const { getProductSuggestions, clearProductSuggestions } =
        useProductSuggestApi();

    const suggestions = useMemo(
        () => flattenCategories(categories),
        [categories],
    );

    const trimmed = query.trim();
    const suggestQuery = toSuggestQuery(query);
    const isPending = suggestQuery !== "" && suggestQuery !== settledQuery;
    const visibleProducts = suggestQuery ? productSuggestions : NO_PRODUCTS;
    const visibleProductsStatus = suggestQuery ? productsStatus : "idle";

    // An empty (or whitespace-only) query owns no options: `includes("")` would otherwise match
    // every category, and ArrowDown + Enter on an empty input would navigate somewhere the
    // shopper never saw. Gated on the same `trimmed` that decides whether the listbox renders.
    const options = useMemo(
        () =>
            trimmed
                ? buildAutocompleteOptions(trimmed, suggestions, visibleProducts)
                : NO_OPTIONS,
        [trimmed, suggestions, visibleProducts],
    );
    // Product rows arrive after the keystroke that highlighted a category, and a new query can
    // shorten the list under the highlight; clamping here keeps the highlight on a row that exists.
    const highlightedIndex = activeIndex < options.length ? activeIndex : -1;
    const productCount = options.filter((o) => o.kind === "product").length;
    // Expanded means "there is a listbox with at least one option to control". A status message
    // on its own is not a listbox — it lives in the live region below — so it does not expand
    // the combobox, and there is nothing for aria-controls to point at.
    const isExpanded = options.length > 0;
    // "No matches." is a verdict, so it waits for one: no options, nothing pending, nothing
    // failed. Until the products request settles the live region says what is happening instead.
    const showNoMatches =
        trimmed.length > 0 &&
        options.length === 0 &&
        !isPending &&
        visibleProductsStatus !== "failed";
    const productsMessage = productsLiveMessage(
        isPending,
        visibleProductsStatus,
        productCount,
    );

    // Every path that changes the query drops the highlight: the rows under it are about to
    // change. Dropping below the floor also forgets the settled query, so that the same query
    // typed again after a clear (or a reopen) is searched — and announced — afresh.
    const updateQuery = useCallback((value: string) => {
        setQuery(value);
        setActiveIndex(-1);
        if (!toSuggestQuery(value)) setSettledQuery("");
    }, []);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        updateQuery("");
    }, [setIsOpen, updateQuery]);

    const handleSubmit = useCallback(() => {
        if (!trimmed) return;
        pushRecent(trimmed);
        navigate(`${ROUTES.SEARCH}?q=${encodeURIComponent(trimmed)}`);
        handleClose();
    }, [trimmed, pushRecent, navigate, handleClose]);

    const handleSelectSuggestion = useCallback(
        (categoryId: number) => {
            navigate(ROUTES.BROWSE_CATEGORY(categoryId));
            handleClose();
        },
        [navigate, handleClose],
    );

    const handleSelectProduct = useCallback(
        (productId: string) => {
            navigate(ROUTES.PRODUCT_DETAIL(productId));
            handleClose();
        },
        [navigate, handleClose],
    );

    const handleSelectOption = useCallback(
        (option: AutocompleteOption) => {
            if (option.kind === "category") {
                handleSelectSuggestion(option.category.id);
            } else {
                handleSelectProduct(option.product.id);
            }
        },
        [handleSelectSuggestion, handleSelectProduct],
    );

    const handleSelectRecent = useCallback(
        (term: string) => {
            pushRecent(term);
            navigate(`${ROUTES.SEARCH}?q=${encodeURIComponent(term)}`);
            handleClose();
        },
        [pushRecent, navigate, handleClose],
    );

    // ArrowDown/ArrowUp walk the whole option list — both sections — and wrap at either end.
    // Enter on a highlighted row selects it; Enter with no highlight is left to SearchInput,
    // which submits the query.
    const handleInputKeyDown = useCallback(
        (e: ReactKeyboardEvent<HTMLInputElement>) => {
            if (e.key === "ArrowDown" || e.key === "ArrowUp") {
                // No listbox showing, nothing to move: the native caret move stays the
                // shopper's — hence before preventDefault, not after.
                if (options.length === 0) return;
                e.preventDefault();
                const last = options.length - 1;
                if (e.key === "ArrowDown") {
                    setActiveIndex(
                        highlightedIndex >= last ? 0 : highlightedIndex + 1,
                    );
                } else {
                    setActiveIndex(
                        highlightedIndex <= 0 ? last : highlightedIndex - 1,
                    );
                }
                return;
            }
            if (e.key === "Enter" && highlightedIndex >= 0) {
                e.preventDefault();
                handleSelectOption(options[highlightedIndex]);
            }
        },
        [options, highlightedIndex, handleSelectOption],
    );

    // The empty state's Browse-categories Retry. The atoms carry the outcome
    // (categoriesFetchErrorAtom); the toast is the notification layer's.
    const loadCategories = useCallback(() => {
        getCategories().catch(() => {
            // Notification layer already surfaces API errors; nothing else for us to do.
        });
    }, [getCategories]);

    // Lazy-load categories the first time the panel opens, if not already in the store.
    useEffect(() => {
        if (!isOpen) return;
        if (categories.length > 0) return;
        loadCategories();
    }, [isOpen, categories.length, loadCategories]);

    // Product suggestions, one request per typing burst: every change of the (trimmed) query
    // restarts the timer, and only the query that survives PRODUCT_SUGGEST_DEBOUNCE_MS is sent.
    useEffect(() => {
        if (!suggestQuery) {
            // Below the floor the Products section is absent whatever the atoms hold, so this
            // is the one moment the stale-while-revalidate hold can be released without a
            // flash — and it must be: rows kept here would be the previous query's (or the
            // previous session's, after a close) showing under the next two characters typed.
            clearProductSuggestions();
            return;
        }
        let cancelled = false;
        const timer = setTimeout(() => {
            getProductSuggestions(suggestQuery)
                .catch(() => {
                    // The hook has already published `failed` to the status atom, which the
                    // live region renders; there is no toast by design.
                })
                .finally(() => {
                    // Latest-wins: once a newer query has taken over, this read's settle is
                    // not the one `isPending` is waiting for.
                    if (!cancelled) setSettledQuery(suggestQuery);
                });
        }, PRODUCT_SUGGEST_DEBOUNCE_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [suggestQuery, getProductSuggestions, clearProductSuggestions]);

    // Unmount. The cleanup above only disarms the timer; a request already on the wire would
    // otherwise run to completion — and publish — for a panel that no longer exists.
    useEffect(
        () => () => clearProductSuggestions(),
        [clearProductSuggestions],
    );

    // Focus management + scroll lock + Esc + Tab trap. The Browse-categories
    // Retry unmounts under focus when it is pressed. The backdrop below is a
    // sibling of the panel, so a drag out of the panel never clicks it: its
    // plain onClick stays.
    const { panelProps } = useDialogFocusTrap({
        panelRef,
        isOpen,
        isProcessing: false,
        onRequestClose: handleClose,
        initialFocusSelector: SEARCH_INPUT_SELECTOR,
    });

    if (!isOpen) return null;

    return (
        <>
            <div
                onClick={handleClose}
                className="fixed inset-0 bg-black/30 z-40"
            />
            <div
                ref={panelRef}
                data-testid="search-panel"
                role="dialog"
                aria-modal="true"
                aria-label="Search"
                {...panelProps}
                className="fixed z-50 left-0 right-0 top-0 md:top-20 md:left-1/2 md:right-auto md:-translate-x-1/2 md:w-[32rem] md:max-w-[90vw] bg-base-100 text-neutral shadow-xl md:rounded-lg flex flex-col max-h-[100vh] md:max-h-[70vh] overflow-hidden focus:outline-none"
            >
                <div className="flex items-center gap-2 p-3 border-b border-base-200">
                    <SearchInput
                        ref={inputRef}
                        value={query}
                        onChange={updateQuery}
                        onSubmit={handleSubmit}
                        onClear={() => {
                            updateQuery("");
                            inputRef.current?.focus();
                        }}
                        onKeyDown={handleInputKeyDown}
                        isExpanded={isExpanded}
                        listboxId={LISTBOX_ID}
                        activeDescendantId={
                            highlightedIndex >= 0
                                ? optionElementId(highlightedIndex)
                                : undefined
                        }
                    />
                    <button
                        type="button"
                        aria-label="Close search"
                        onClick={handleClose}
                        className="btn btn-ghost btn-sm btn-square text-neutral"
                    >
                        <Icon name="close" className="text-2xl" />
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto">
                    {trimmed ? (
                        <Autocomplete
                            query={trimmed}
                            listboxId={LISTBOX_ID}
                            options={options}
                            activeIndex={highlightedIndex}
                            showNoMatches={showNoMatches}
                            onSelect={handleSelectOption}
                        />
                    ) : (
                        <>
                            <RecentSearches
                                items={recent}
                                onSelect={handleSelectRecent}
                            />
                            {/* Top-level categories only: the roots of the
                                tree. A fresh shopper has no recents, so this
                                is what the panel opens onto (#401). */}
                            <BrowseCategories
                                categories={categories}
                                isLoading={isFetchingCategories}
                                hasError={categoriesFetchError}
                                onSelect={handleSelectSuggestion}
                                onRetry={loadCategories}
                            />
                        </>
                    )}
                    {/* The Products status, OUTSIDE the listbox: a message is not an option and
                        cannot be an owned child of a listbox or group. Always mounted while the
                        panel is open so a screen reader hears each change; visible for the two
                        states a sighted shopper needs where the rows would be (searching, failed),
                        screen-reader-only for the result count, which the rows already show. */}
                    <div
                        aria-live="polite"
                        data-testid="autocomplete-products-status"
                        className={
                            productsMessage?.visible
                                ? "px-4 py-3 text-sm text-neutral/60"
                                : "sr-only"
                        }
                    >
                        {productsMessage?.text}
                    </div>
                </div>
            </div>
        </>
    );
};
